import { defineStore } from 'pinia'
import { inject } from 'vue';
import { useCustomerStore } from './customer';
import { inRange, WEEK_DAYS } from '@/client/js/shared/utils';

export const useProductStore = defineStore('products', {
  state: () => ({
    items: {},
    modifierGroups: {},
    modifiers: {},
    taxRates: {},
    ids: [],
    categories: [],
  }),
  getters: {
    list: function () {
      return this.ids.map(i => this.items[i])
    },
    loaded: function () {
      return this.ids.length > 0
    },
    categoryTree: function () {
      const all = this.filteredCategories;
      const byId = Object.fromEntries(all.map(c => [c.id, c]));

      // Top-level = categories with no parent (or parent not in current set)
      return all
        .filter(cat => !cat.parent) 
        .map(cat => ({
          ...cat,
          children: (cat.subcategories || [])
            .map(subId => byId[subId])
            .filter(Boolean),
        }));
    },
    filteredCategories: function () {
      let filteredCategories = this.categories;

      const customerStore = useCustomerStore();
      const currentOrderType = customerStore.orderType;

      // fallback if Order Type not exist
      if (!currentOrderType || !window._EZCL?.data?.services?.[customerStore.orderType]) {
        console.debug(`🏪 Categories: fallback (${currentOrderType} service not exist)`)
        return this.categories;
      }
      const serviceConfig = window._EZCL?.data?.services?.[currentOrderType];
      let allowedCategoryIds = serviceConfig?.categories;
      if (!allowedCategoryIds || allowedCategoryIds.length === 0) {
        // fallback if categories doesnt specified for Order Type
        console.debug(`🏪 Categories: all categories for ${currentOrderType} service`)
      } else {
        let allowedIds = allowedCategoryIds.map(Number);
        console.debug(`🏪 Categories: filtered for ${currentOrderType} service (${allowedIds.join(', ')})`);
        filteredCategories = this.categories.filter(cat => {
          return allowedIds.includes(Number(cat.id)) || allowedIds.includes(Number(cat?.parent))
        })
      }

      // Dayparts
      const dayparts = window?._EZCL?.data?.ordering?.dayparts ?? []
      if (!Array.isArray(dayparts) || dayparts.length === 0) {
        console.debug(`🏪 Categories: no dayparts`)
      } else {
        console.debug(`🏪 Categories: Dayparts (${dayparts.length}) found`)
        const now = new Date()
        const nowDayKey = WEEK_DAYS[now.getDay()]
        const nowMin = now.getHours() * 60 + now.getMinutes()
        const allowed = new Set()

        dayparts.forEach(dp => {
          if (!dp?.categories?.length) return
          if (!dp.days?.includes(nowDayKey)) return
          if (!inRange(dp.from, dp.to, nowMin)) return
          dp.categories.forEach(id => allowed.add(Number(id)))
        })

        const filteredByDayparts = filteredCategories.filter(cat => allowed.has(Number(cat.id)));
        filteredCategories = filteredByDayparts.length === 0 ? filteredCategories : filteredByDayparts;
      }

      return filteredCategories;
    }
  },
  actions: {
    getDefaultTaxRates: function () {
      return Object.values(this.taxRates).filter(taxRate => taxRate?.isDefault)
    },
    getItemTaxRates: function (id) {
      if (!id || !this.items[id]) {
        if (!this.loaded) {
          console.debug('🏪 getItemTaxRates: is not loaded yet for product %s', id);
        } else {
          console.debug('🏪 getItemTaxRates: undefined tax rate product for %s', id);
        }
        return [];
      }
      let item = this.items[id];
      if (item?.defaultTaxRates == true) {
        console.debug('🏪 %s: Default Tax Rate', id);
        return this.getDefaultTaxRates();
      } else if (Array.isArray(item?.taxRateIds)) {
        console.debug('🏪 %s: Custom Tax Rate', id);
        return Object.values(this.taxRates).filter(taxRate => item.taxRateIds.includes(taxRate.id))
      } else {
        console.debug('🏪 %s: Unknown Tax Rate', id);
        return [];
      }
    },
    async fetchAll() {
      console.debug('🏪 Products: before fetching start');
      if (this.loaded) {
        console.debug('🏪 Products: already loaded')
        return;
      }

      console.debug('🏪 Products: API requested');
      const response = await inject('restApi').get('store');
      const data = response.data;

      this.items = data?.items;
      this.modifierGroups = data?.modifierGroups;
      this.categories = data?.categories;
      this.modifiers = data?.modifiers;
      this.tags = data?.tags;
      this.taxRates = data?.taxRates;
      console.log(data?.categories);
      console.debug('🏪 Products: API loaded');

      // const response = await inject('restApi').get('products', { params: { extended: 1 } } );
      // this.ids = data.map((product) => {
      //   this.items[product.id] = product
      //   return product.id
      // })
    },
  },
})